import { useEffect, useRef, useState } from 'react';
import {
  checkYoutubeVideoAvailability,
  getYoutubeApiKey,
} from './youtube-api-check';

const ERROR_MESSAGES = [
  'não está disponível',
  'não disponível',
  'privado',
  'private',
  'removido',
  'removed',
  'not available',
  'unavailable',
  'não permite a reprodução',
  'does not allow playback',
  'has been removed',
  'was removed',
  'vídeo indisponível',
  'video unavailable',
  'bloqueado',
  'blocked',
  'direitos autorais',
  'copyright',
  'grupo globo',
];

const POLLING_INTERVAL = 300;
const MAX_POLLING_TIME = 4000;
const ERROR_TIMEOUT = 6000;
const SUCCESS_TIMEOUT = 2000;

export function useYoutubeErrorDetector(youtubeId) {
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(false);
  const pollingRef = useRef(null);
  const timeoutRef = useRef(null);
  const iframeRef = useRef(null);
  const postMessageListenerRef = useRef(null);
  const successTimeoutRef = useRef(null);

  useEffect(() => {
    if (!youtubeId) {
      setStatus('idle');
      setError(false);
      return;
    }

    let cancelled = false;

    const apiKey = getYoutubeApiKey();

    // Verificação prévia via API - PRIORIDADE MÁXIMA
    if (apiKey) {
      checkYoutubeVideoAvailability(youtubeId, apiKey).then((result) => {
        if (cancelled) return;

        if (result.available === false) {
          setError(true);
          setStatus('error');
          return;
        }

        if (result.available === true) {
          setStatus('loading');
          setError(false);
          startFallbackDetection();
        } else {
          // API error - usar fallback
          setStatus('loading');
          setError(false);
          startFallbackDetection();
        }
      });
    } else {
      // Sem API key - usar apenas fallback
      setStatus('loading');
      setError(false);
      startFallbackDetection();
    }

    function startFallbackDetection() {
      let pollingStartTime = null;
      let pollAttempts = 0;

      const checkIframeError = () => {
        const iframe = iframeRef.current;
        if (!iframe) return false;

        try {
          const iframeDoc =
            iframe.contentDocument || iframe.contentWindow?.document;
          if (iframeDoc && iframeDoc.body) {
            const bodyText = iframeDoc.body.innerText?.toLowerCase() || '';
            const hasError = ERROR_MESSAGES.some((msg) =>
              bodyText.includes(msg.toLowerCase())
            );

            if (hasError) {
              return true;
            }

            const errorElements = iframeDoc.querySelectorAll(
              '[class*="error"], [id*="error"], [class*="unavailable"], [class*="blocked"]'
            );
            if (errorElements.length > 0) {
              return true;
            }
          }
        } catch (e) {
          // CORS bloqueado - continuar com outras estratégias
        }
        return false;
      };

      const checkDimensions = () => {
        const iframe = iframeRef.current;
        if (!iframe) return false;

        const rect = iframe.getBoundingClientRect();
        const hasInvalidDimensions =
          rect.width === 0 || rect.height === 0 || rect.width < 100;

        return hasInvalidDimensions;
      };

      const startPolling = () => {
        pollingStartTime = Date.now();
        pollingRef.current = setInterval(() => {
          if (cancelled) return;
          pollAttempts++;
          const elapsed = Date.now() - pollingStartTime;

          if (elapsed > MAX_POLLING_TIME) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
            if (status === 'loading') {
              setStatus('playing');
            }
            return;
          }

          const hasError = checkIframeError();
          const hasInvalidDimensions = checkDimensions();

          if (hasError || hasInvalidDimensions) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
            setError(true);
            setStatus('error');
            return;
          }

          const iframe = iframeRef.current;
          if (iframe && iframe.offsetWidth > 0 && iframe.offsetHeight > 0) {
            if (pollAttempts > 3) {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
              if (!successTimeoutRef.current) {
                successTimeoutRef.current = setTimeout(() => {
                  if (!cancelled && !error) {
                    setStatus('playing');
                  }
                }, SUCCESS_TIMEOUT);
              }
            }
          }
        }, POLLING_INTERVAL);
      };

      const setupPostMessageListener = () => {
        postMessageListenerRef.current = (event) => {
          if (cancelled) return;

          if (
            event.origin !== 'https://www.youtube.com' &&
            event.origin !== 'https://www.youtube-nocookie.com'
          ) {
            return;
          }

          const data = event.data;
          if (typeof data === 'string') {
            try {
              const parsed = JSON.parse(data);
              if (parsed && parsed.event === 'video-progress') {
                setStatus('playing');
              }
            } catch (e) {
              // Não é JSON válido
            }
          }

          if (
            data &&
            (data.event === 'onError' ||
              data.info === 'ERROR' ||
              data.event === 'onVideoError')
          ) {
            setError(true);
            setStatus('error');
          }
        };

        window.addEventListener('message', postMessageListenerRef.current);
      };

      timeoutRef.current = setTimeout(() => {
        if (cancelled) return;
        if (status === 'loading' || !error) {
          const hasError = checkIframeError();
          const hasInvalidDimensions = checkDimensions();

          if (hasError || hasInvalidDimensions) {
            setError(true);
            setStatus('error');
          } else if (!error) {
            setStatus('playing');
          }
        }
      }, ERROR_TIMEOUT);

      startPolling();
      setupPostMessageListener();
    }

    const cleanup = () => {
      cancelled = true;
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = null;
      }
      if (postMessageListenerRef.current) {
        window.removeEventListener('message', postMessageListenerRef.current);
        postMessageListenerRef.current = null;
      }
    };

    return cleanup;
  }, [youtubeId, status, error]);

  const registerIframe = (iframe) => {
    iframeRef.current = iframe;
  };

  return {
    status: error ? 'error' : status,
    error,
    registerIframe,
  };
}
